import { useEffect } from "react";
import { useGlobals } from "@/components/providers/globals-provider.tsx";
import { getValidApiToken } from "@/lib/api-token.ts";
import { looseGetRequestBuilder } from "@/lib/fetcher.ts";
import Logger from "@/lib/logger.ts";
import { itemListSetWishlistStatusAction } from "@/redux/slices/item-list-slice.ts";
import { useAppDispatch, useAppSelector } from "@/redux/store.ts";
import { Site } from "@/types";

type WishlistListItem = {
    id: string;
    number?: string;
};

type WishlistListResponse = {
    success: boolean;
    items?: WishlistListItem[];
};

export default function useSmartnumberWishlistStatus() {
    const { site, locale, apiTokenUrl } = useGlobals();

    const dispatch = useAppDispatch();
    const smartNumber = useAppSelector((state) => state.smartNumber.calculatedResult);
    const wishlistListPath = useAppSelector((state) => state.itemList.wishlistListPath);

    const isNorthAmerica = site === Site.NorthAmerica;
    const canSyncStatus =
        !isNorthAmerica && !!smartNumber && !!wishlistListPath && !!apiTokenUrl;

    useEffect(() => {
        if (!canSyncStatus) return;

        const abortController = new AbortController();

        const syncStatus = async () => {
            const token = await getValidApiToken(apiTokenUrl!);
            if (!token || abortController.signal.aborted) return;

            const url = new URL(wishlistListPath!);
            const get = looseGetRequestBuilder(site, locale);

            try {
                const response = await get<WishlistListResponse, { success: false }>(url, {
                    authorization: token,
                    query: { "types[]": ["smartnumber"] },
                });

                if (abortController.signal.aborted) return;

                if (!response.success) {
                    dispatch(itemListSetWishlistStatusAction(false));
                    return;
                }

                const isOnWishlist = (response.items ?? []).some(
                    (item) => item.id === smartNumber || item.number === smartNumber,
                );

                dispatch(itemListSetWishlistStatusAction(isOnWishlist));
            } catch (err) {
                if (!abortController.signal.aborted) {
                    Logger.error("Failed to sync smartnumber wishlist status", err);
                }
            }
        };

        void syncStatus();

        return () => {
            abortController.abort();
        };
    }, [apiTokenUrl, canSyncStatus, dispatch, locale, site, smartNumber, wishlistListPath]);
}
